/**
 * Unified recommendation layer.
 *
 * Every audit type produces "recommendations" in a different native shape
 * (bare `string[]` for the Website audits, structured `BrandAxisRecommendation[]`
 * for Brand Perception, `MissingAuthority[]` / `Recommendation[]` for Earned
 * Media). This module normalizes all of them into ONE shape (`UnifiedRecommendation`)
 * so the frontend can render a single professional card everywhere, and provides
 * the deterministic classifier that decides — from the recommendation TEXT +
 * its source dimension + the audit it came from — whether it should become a
 * Task, a content proposal (Studio de création), or a GEO-strategy change.
 *
 * The classifier is a PURE function (no LLM) so it is consistent across the
 * frontend and any backend caller, needs no agent redeploy, and fixes the
 * historical bug where a freshness reco like "Ajouter datePublished en JSON-LD"
 * was hardcoded to the `content` category instead of `schema`.
 */
import type { AuditType } from './audit';
import type { TaskCategory } from './task';
import type { BrandAxisRecommendation, MissingAuthority, Recommendation } from './audit';

/** What a recommendation should turn into when the user acts on it. */
export type RecommendationTarget = 'task' | 'content' | 'geo_strategy';

export type RecommendationLevel = 'high' | 'medium' | 'low';

/**
 * The normalized recommendation the unified card renders. `impact` / `effort`
 * are optional because some sources (Website audits) don't provide them; the
 * card simply omits the chips when absent.
 */
export interface UnifiedRecommendation {
  /** Stable-ish id for React keys + optimistic add tracking (may be derived). */
  id: string;
  title: string;
  description?: string;
  impact?: RecommendationLevel;
  effort?: RecommendationLevel;
  /** Where the action routes: Tasks, Studio de création, or GEO Strategy. */
  target: RecommendationTarget;
  /** Task category (only meaningful when target === 'task'). */
  category: TaskCategory;
  /** Provenance — which page the finding is about (Website audits). */
  sourcePageUrl?: string;
  /** Provenance — the audit sub-dimension (e.g. "schema", "content_freshness"). */
  sourceDimension?: string;
  /** Earned-media target domain, when the reco is about acquiring/fixing a media. */
  targetDomain?: string;
  /** The audit this came from, for provenance display + task metadata. */
  auditType: AuditType;
  /** Human-readable origin label, e.g. "Autorité", "Fraîcheur", "Médias acquis". */
  originLabel: string;
  /** Optional anchor back to the source query (Brand Perception). */
  queryTextHash?: string;
}

// --------------------------------------------------------------------------
// Deterministic classifier
// --------------------------------------------------------------------------

/**
 * Keyword buckets, most-specific first. Order matters: a reco mentioning both
 * "article" and "JSON-LD" is technical, not content, so schema/technical
 * patterns are tested before content patterns.
 *
 * All patterns are accent-insensitive-ish and lower-cased at match time.
 */
const SCHEMA_PATTERNS = [
  'json-ld',
  'jsonld',
  'schema.org',
  'schema ',
  'donnees structurees',
  'données structurées',
  'donnee structuree',
  'microdata',
  'rich snippet',
  'datepublished',
  'datemodified',
  'balisage',
  'markup',
];

const TECHNICAL_PATTERNS = [
  'balise',
  '<title>',
  'meta ',
  'meta-',
  'metadonnee',
  'métadonnée',
  'hreflang',
  'canonical',
  'robots.txt',
  'llms.txt',
  'sitemap',
  'redirection',
  'redirect',
  'temps de chargement',
  'vitesse',
  'code html',
  'html ',
  'attribut alt',
  'balise alt',
  'header http',
  'en-tete',
  'en-tête',
  'structure hn',
  'hierarchie de titres',
  'hiérarchie de titres',
  'h1',
  'h2',
];

const CONTENT_PATTERNS = [
  'redige',
  'rédige',
  'redaction',
  'rédaction',
  'ecris',
  'écris',
  'publie un article',
  'publier un article',
  'creer un article',
  'créer un article',
  'article de blog',
  'billet de blog',
  'paragraphe',
  'contenu editorial',
  'contenu éditorial',
  'reecris',
  'réécris',
  'reformule',
  'etude de cas',
  'étude de cas',
  'temoignage',
  'témoignage',
  'faq',
  'guide pratique',
  'livre blanc',
  'page de contenu',
  'enrichir le contenu',
  'ajouter du contenu',
  'developper le sujet',
  'développer le sujet',
];

const GEO_STRATEGY_PATTERNS = [
  'angle strategique',
  'angle stratégique',
  'positionnement',
  'pilier de contenu',
  'content pillar',
  'requete cible',
  'requête cible',
  'target quer',
  'strategie geo',
  'stratégie geo',
  'source d autorite',
  "source d'autorité",
  'axe de differenciation',
  'axe de différenciation',
  'proposition de valeur',
  'redefinir la strategie',
  'redéfinir la stratégie',
];

const EXTERNAL_PATTERNS = [
  'presse',
  'journaliste',
  'media ',
  'média ',
  'medias',
  'médias',
  'backlink',
  'lien entrant',
  'annuaire',
  'wikipedia',
  'wikidata',
  'relations presse',
  'communique',
  'communiqué',
  'partenariat',
  'influenceur',
  'avis client',
  'plateforme d avis',
  "plateforme d'avis",
  'contacter',
  'pitch',
];

const SOCIAL_PATTERNS = [
  'linkedin',
  'twitter',
  'reseau social',
  'réseau social',
  'reseaux sociaux',
  'réseaux sociaux',
  'post social',
  'publication sociale',
];

function normalize(s: string): string {
  return (s || '').toLowerCase();
}

function anyMatch(haystack: string, patterns: string[]): boolean {
  return patterns.some((p) => haystack.includes(p));
}

export interface ClassifyInput {
  /** The recommendation text (title, or title + description concatenated). */
  text: string;
  /** The source sub-dimension, when known (e.g. "schema", "content_freshness"). */
  dimension?: string;
  /** The audit this came from — biases classification by axis. */
  auditType: AuditType;
}

export interface ClassifyResult {
  target: RecommendationTarget;
  category: TaskCategory;
}

/**
 * Decide the target + task category for a recommendation, deterministically.
 *
 * DIMENSION HARD-GATES come first — a Website-audit finding's sub-dimension is
 * an authoritative signal that OVERRIDES keyword guessing, because those
 * findings are structural/editorial-fix work, never "generate a new article":
 *  - `content_freshness`  → technical TASK (schema if it's about JSON-LD dates).
 *    "Ajouter un horodatage 'Dernière mise à jour'" is a technical task, not content.
 *  - `structural_seo` (+ its sub-dims: headings/meta/links/…) → schema or
 *    technical/seo TASK. Never content.
 *  - `content_quality`    → geo_strategy if it's really a positioning change,
 *    else a TASK (persona "contenu"). Never the Studio (`content` target) — the
 *    Studio generates NEW articles, which is not what an on-page quality fix is.
 *
 * The `content` target (Studio de création) is reserved for Brand Perception /
 * Earned Media recos that genuinely call for publishing a new piece — a Website
 * audit NEVER routes to the Studio.
 *
 * After the gates, text keyword buckets decide (most-specific first: schema →
 * technical → geo-strategy → content → external → social), then an audit-type
 * fallback.
 */
export function classifyRecommendation(input: ClassifyInput): ClassifyResult {
  const text = normalize(input.text);
  const dim = normalize(input.dimension || '');
  const isSchemaText = anyMatch(text, SCHEMA_PATTERNS);
  const isGeoStrategyText = anyMatch(text, GEO_STRATEGY_PATTERNS);

  // ── Dimension hard-gates (Website audit) ──
  if (dim === 'schema') return { target: 'task', category: 'schema' };

  if (dim === 'content_freshness') {
    // Freshness fixes are technical (visible timestamps, machine-readable dates).
    return { target: 'task', category: isSchemaText ? 'schema' : 'technical' };
  }

  if (dim === 'content_quality') {
    // On-page content quality → a strategy change or a "contenu" task, never Studio.
    if (isGeoStrategyText) return { target: 'geo_strategy', category: 'external' };
    return { target: 'task', category: 'content' };
  }

  // Any OTHER website dimension is a structural-SEO sub-dimension → technical/seo.
  if (dim && input.auditType === 'website') {
    if (isSchemaText) return { target: 'task', category: 'schema' };
    if (anyMatch(text, CONTENT_PATTERNS) && !anyMatch(text, TECHNICAL_PATTERNS)) {
      return { target: 'task', category: 'content' };
    }
    return { target: 'task', category: anyMatch(text, TECHNICAL_PATTERNS) ? 'technical' : 'seo' };
  }

  // ── Text keyword buckets (specific → general) ──
  if (isSchemaText) return { target: 'task', category: 'schema' };
  if (anyMatch(text, TECHNICAL_PATTERNS)) return { target: 'task', category: 'technical' };
  if (isGeoStrategyText) return { target: 'geo_strategy', category: 'external' };
  if (anyMatch(text, CONTENT_PATTERNS)) {
    // Studio only for non-website audits; a website content reco stays a task.
    return input.auditType === 'website'
      ? { target: 'task', category: 'content' }
      : { target: 'content', category: 'content' };
  }
  if (anyMatch(text, SOCIAL_PATTERNS)) return { target: 'task', category: 'social' };
  if (anyMatch(text, EXTERNAL_PATTERNS)) return { target: 'task', category: 'external' };

  // ── Audit-type fallback ──
  switch (input.auditType) {
    case 'earned_media':
      return { target: 'task', category: 'earned_media' };
    case 'website':
      // A website reco that hit no bucket is an on-page fix → SEO task, NOT Studio.
      return { target: 'task', category: 'seo' };
    case 'brand_narrative':
    case 'brand_authority':
    case 'brand_referral':
    case 'brand':
      return { target: 'task', category: 'external' };
    default:
      return { target: 'task', category: 'external' };
  }
}

// --------------------------------------------------------------------------
// Adapters — native audit shapes → UnifiedRecommendation[]
// --------------------------------------------------------------------------

/** Small, stable id from a string (djb2) — for React keys / add-tracking. */
function hashId(prefix: string, s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = (h * 33) ^ s.charCodeAt(i);
  return `${prefix}_${(h >>> 0).toString(36)}`;
}

/** Split a plain-text reco into a title (first sentence, ≤120) + remainder. */
function splitTitle(text: string): { title: string; description?: string } {
  const clean = (text || '').trim();
  if (clean.length <= 120) return { title: clean };
  // Break at the first sentence boundary if there is one early enough.
  const dot = clean.slice(0, 160).search(/[.:!?]\s/);
  if (dot > 20 && dot < 118) {
    return { title: clean.slice(0, dot + 1).trim(), description: clean.slice(dot + 1).trim() };
  }
  return { title: clean.slice(0, 117).trimEnd() + '…', description: clean };
}

/** Normalize a bare `string[]` recommendation list (Website audits). */
export function fromStringList(
  items: string[] | undefined,
  ctx: { auditType: AuditType; originLabel: string; sourcePageUrl?: string; dimension?: string },
): UnifiedRecommendation[] {
  return (items || []).filter(Boolean).map((raw) => {
    const { title, description } = splitTitle(raw);
    const { target, category } = classifyRecommendation({
      text: raw,
      dimension: ctx.dimension,
      auditType: ctx.auditType,
    });
    return {
      id: hashId('s', `${ctx.originLabel}:${ctx.sourcePageUrl || ''}:${raw}`),
      title,
      description,
      target,
      category,
      sourcePageUrl: ctx.sourcePageUrl,
      sourceDimension: ctx.dimension,
      auditType: ctx.auditType,
      originLabel: ctx.originLabel,
    };
  });
}

/** Normalize Brand Perception `recommendations_v2` (already structured). */
export function fromBrandAxis(
  items: BrandAxisRecommendation[] | undefined,
  ctx: { auditType: AuditType; originLabel: string },
): UnifiedRecommendation[] {
  return (items || []).map((r) => {
    const { target, category } = classifyRecommendation({
      text: `${r.title} ${r.detail || ''}`,
      auditType: ctx.auditType,
    });
    return {
      id: hashId('b', `${ctx.originLabel}:${r.title}`),
      title: r.title,
      description: r.detail,
      impact: r.impact,
      effort: r.effort,
      target,
      category,
      auditType: ctx.auditType,
      originLabel: ctx.originLabel,
      queryTextHash: r.queryTextHash,
    };
  });
}

/** Normalize Earned Media `MissingAuthority[]` (media to conquer). */
export function fromMissingAuthorities(
  items: MissingAuthority[] | undefined,
  ctx: { originLabel: string },
): UnifiedRecommendation[] {
  return (items || []).map((m) => {
    const impact: RecommendationLevel =
      m.leverageScore >= 66 ? 'high' : m.leverageScore >= 33 ? 'medium' : 'low';
    return {
      id: hashId('m', `${m.domain}`),
      title: m.suggestedAngle || `Obtenir une couverture sur ${m.domain}`,
      description: m.rationale,
      impact,
      effort: m.effort,
      target: 'task',
      category: 'external',
      targetDomain: m.domain,
      auditType: 'earned_media',
      originLabel: ctx.originLabel,
    };
  });
}

/** Normalize Earned Media categorized `Recommendation[]`. */
export function fromEarnedMediaRecos(
  items: Recommendation[] | undefined,
  ctx: { originLabel: string },
): UnifiedRecommendation[] {
  return (items || []).map((r) => {
    const { title, description } = splitTitle(r.text);
    const { target, category } = classifyRecommendation({
      text: r.text,
      auditType: 'earned_media',
    });
    return {
      id: hashId('e', `${ctx.originLabel}:${r.targetDomain || ''}:${r.text}`),
      title,
      description,
      impact: r.severity,
      target,
      category,
      targetDomain: r.targetDomain,
      auditType: 'earned_media',
      originLabel: ctx.originLabel,
    };
  });
}
